import { useQuery } from '@tanstack/vue-query';
import { toValue, computed, type MaybeRefOrGetter } from 'vue';
import { useUserState } from '@/stores/useUserState';
import {
  getCategoryTree,
  getCategoryMarkets,
  getMarketContracts,
  searchContracts,
  getContractDetails,
  getMassContractDetails,
  getElectionMap,
  getElectionTopOI,
  getElectionGrid,
  getElectionStateGrid,
} from './forecasttrader.api';
import type {
  ForecastTraderCategoryMarketsRequest,
  ForecastTraderMarketContractsParams,
  ForecastTraderContractSearchParams,
  ForecastTraderContractDetailsParams,
  ForecastTraderMassContractDetailsParams,
  ForecastTraderElectionMapParams,
  ForecastTraderElectionTopOIParams,
  ForecastTraderElectionGridParams,
  ForecastTraderElectionStateGridParams,
} from '@/types/forecasttrader.types';
import {
  filterMarketsToForecastX,
  filterMassDetailsToForecastX,
  filterSearchToForecastX,
} from '@/utils/filterToForecastX';

export const useGetCategoryTree = (enabled: MaybeRefOrGetter<boolean> = true) => {
  const userState = useUserState();
  return useQuery({
    queryKey: computed(() => ['forecasttrader', 'category', 'tree', userState.usePublicApi]),
    queryFn: () => getCategoryTree(userState.usePublicApi),
    enabled: () => toValue(enabled),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
};

export const useGetCategoryMarkets = (markets: MaybeRefOrGetter<ForecastTraderCategoryMarketsRequest>) => {
  const userState = useUserState();
  const queryKey = computed(() => ['forecasttrader', 'market', 'category', toValue(markets), userState.usePublicApi]);

  return useQuery({
    queryKey,
    queryFn: () => getCategoryMarkets(toValue(markets), userState.usePublicApi),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: () => {
      const marketsValue = toValue(markets);
      return marketsValue.markets && marketsValue.markets.length > 0;
    },
    select: filterMarketsToForecastX,
  });
};

export const useGetMarketContracts = (params: MaybeRefOrGetter<ForecastTraderMarketContractsParams>) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => {
      const paramsValue = toValue(params);
      // Create a clean, serializable query key that properly handles undefined values
      return [
        'forecasttrader',
        'contract',
        'market',
        {
          underlyingConid: paramsValue.underlyingConid || null,
          productConid: paramsValue.productConid || null,
        },
        userState.usePublicApi,
      ];
    }),
    queryFn: () => getMarketContracts(toValue(params), userState.usePublicApi),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: () => {
      const paramsValue = toValue(params);
      // Only run query when we have valid (non-zero) conids
      return !!(
        (paramsValue.underlyingConid && paramsValue.underlyingConid > 0) ||
        (paramsValue.productConid && paramsValue.productConid > 0)
      );
    },
  });
};

export const useSearchContracts = (params: MaybeRefOrGetter<ForecastTraderContractSearchParams>) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => ['forecasttrader', 'contract', 'search', toValue(params), userState.usePublicApi]),
    queryFn: () => searchContracts(toValue(params), userState.usePublicApi),
    staleTime: 2 * 60 * 1000, // 2 minutes - search results can change
    enabled: () => {
      const paramsValue = toValue(params);
      return !!paramsValue.prefix && paramsValue.prefix.trim().length > 0;
    },
    select: filterSearchToForecastX,
  });
};

export const useGetContractDetails = (params: MaybeRefOrGetter<ForecastTraderContractDetailsParams>) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => {
      const paramsValue = toValue(params);
      return ['forecasttrader', 'contract', 'details', { conid: paramsValue.conid || null }, userState.usePublicApi];
    }),
    queryFn: () => getContractDetails(toValue(params), userState.usePublicApi),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: () => {
      const paramsValue = toValue(params);
      return !!paramsValue.conid;
    },
  });
};

export const useGetMassContractDetails = (params: MaybeRefOrGetter<ForecastTraderMassContractDetailsParams>) => {
  const userState = useUserState();
  const queryKey = computed(
    () => ['forecasttrader', 'contract', 'mass-details', toValue(params), userState.usePublicApi] as const,
  );

  return useQuery({
    queryKey,
    queryFn: () => getMassContractDetails(toValue(params), userState.usePublicApi),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: () => {
      const paramsValue = toValue(params);
      return paramsValue.conids && paramsValue.conids.length > 0;
    },
    select: filterMassDetailsToForecastX,
  });
};

export const useGetElectionMap = (params: MaybeRefOrGetter<ForecastTraderElectionMapParams | null>) => {
  const userState = useUserState();
  // Object-based queryKey for clean serialization
  const queryKey = computed(() => ['forecasttrader', 'election', 'map', toValue(params), userState.usePublicApi]);

  return useQuery({
    queryKey,
    queryFn: () => getElectionMap(toValue(params)!, userState.usePublicApi),
    staleTime: 5 * 60 * 1000, // 5 minutes - election map changes frequently
    enabled: () => {
      const paramsValue = toValue(params);
      return paramsValue !== null && !!paramsValue.office && !!paramsValue.raceType;
    },
  });
};

export const useGetElectionTopOI = (params: MaybeRefOrGetter<ForecastTraderElectionTopOIParams>) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => {
      const paramsValue = toValue(params);
      return [
        'forecasttrader',
        'election',
        'top-oi',
        paramsValue ? { productConid: paramsValue.productConid } : null,
        userState.usePublicApi,
      ];
    }),
    queryFn: () => getElectionTopOI(toValue(params), userState.usePublicApi),
    staleTime: 2 * 60 * 1000, // 2 minutes - open interest changes frequently
    enabled: () => {
      const paramsValue = toValue(params);
      return !!paramsValue?.productConid;
    },
  });
};

export const useGetElectionGrid = (params: MaybeRefOrGetter<ForecastTraderElectionGridParams | null>) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => {
      const paramsValue = toValue(params);
      if (!paramsValue) return ['forecasttrader', 'election', 'grid', null];
      // Create a clean, serializable query key
      return [
        'forecasttrader',
        'election',
        'grid',
        {
          office: paramsValue.office,
          raceType: paramsValue.raceType || null,
          party: paramsValue.party || null,
          sortBy: paramsValue.sorting?.product || null,
          state: paramsValue.filters?.product?.state || null,
          productConids: paramsValue.filters?.product?.productConids || null,
          minPrice: paramsValue.filters?.contract?.min_price || null,
          maxPrice: paramsValue.filters?.contract?.max_price || null,
          contractParty: paramsValue.filters?.contract?.party || null,
        },
        userState.usePublicApi,
      ];
    }),
    queryFn: () => getElectionGrid(toValue(params)!, userState.usePublicApi),
    retry: false,
    staleTime: 2 * 60 * 1000, // 2 minutes - election trading data changes frequently
    enabled: () => toValue(params) !== null,
  });
};

export const useGetElectionStateGrid = (params: MaybeRefOrGetter<ForecastTraderElectionStateGridParams | null>) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => {
      const paramsValue = toValue(params);
      return [
        'forecasttrader',
        'election',
        'state-grid',
        paramsValue
          ? {
              offices: paramsValue.offices || null,
              raceType: paramsValue.raceType || null,
              party: paramsValue.party || null,
              sortBy: paramsValue.sorting?.product || null,
              productConids: paramsValue.filters?.product?.productConids || null,
              minPrice: paramsValue.filters?.contract?.min_price || null,
              maxPrice: paramsValue.filters?.contract?.max_price || null,
              contractParty: paramsValue.filters?.contract?.party || null,
            }
          : null,
        userState.usePublicApi,
      ];
    }),
    queryFn: () => getElectionStateGrid(toValue(params)!, userState.usePublicApi),
    retry: false,
    staleTime: 2 * 60 * 1000, // 2 minutes - election trading data changes frequently
    enabled: () => toValue(params) !== null,
  });
};
// last modified by AI tools on 2026-10-09
